import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, fmt, fmtDate, formatApiError, todayISO } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  Phone, MessageSquare, CalendarClock, AlertTriangle, CheckCircle2,
  X, Save, Trash2, History, MapPin, ChevronDown, Award, RefreshCcw, Search,
} from "lucide-react";

const FILTERS = [
  { key: "due",        label: "Due / Overdue" },
  { key: "today",      label: "Due today" },
  { key: "overdue",    label: "Overdue" },
  { key: "upcoming",   label: "Upcoming" },
  { key: "unscheduled",label: "No follow-up" },
  { key: "all",        label: "All pending" },
];

const AMOUNT_BANDS = [
  { key: "any",  label: "Any amount",     min: 0 },
  { key: "10k",  label: "Above ₹10,000",  min: 10000 },
  { key: "50k",  label: "Above ₹50,000",  min: 50000 },
  { key: "1L",   label: "Above ₹1 Lakh",  min: 100000 },
];

const CREDIT_RATINGS = ["", "A", "B", "C", "D"];

const MODE_OPTS = [
  { key: "call",           label: "Call",           Icon: Phone },
  { key: "whatsapp",       label: "WhatsApp",        Icon: MessageSquare },
  { key: "personal_visit", label: "Personal visit",  Icon: MapPin },
  { key: "other",          label: "Other",           Icon: ChevronDown },
];

function LogModal({ row, onClose, onSaved }) {
  const [comment,  setComment]  = useState("");
  const [mode,     setMode]     = useState("call");
  const [nextDate, setNextDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 3);
    return d.toISOString().slice(0, 10);
  });
  const [status, setStatus]     = useState(row.status || "open");
  const [busy, setBusy]         = useState(false);
  const [showHist, setShowHist] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/followups/${row.party_id}/log`, {
        comment, mode, next_followup_date: nextDate || null, status,
      });
      toast.success("Follow-up logged");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const del = async () => {
    if (!(row.id)) return;
    if (!window.confirm("Clear all follow-up history for this customer?")) return;
    try {
      await api.delete(`/followups/${row.id}`);
      toast.success("Follow-up cleared");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm w-full max-w-lg max-h-[92vh] overflow-auto"
        onClick={(e) => e.stopPropagation()} data-testid="followup-modal">
        <div className="p-5 border-b border-gray-200 flex items-start justify-between sticky top-0 bg-white z-10">
          <div className="min-w-0">
            <div className="small-label text-gray-500">Log follow-up · #{(row.followup_count || 0) + 1}</div>
            <div className="font-heading font-black text-xl truncate">{row.party_name}</div>
            <div className="text-xs text-gray-500 mt-1">
              Pending <span className="data-num font-bold text-[#B45309]">₹ {fmt(row.balance)}</span>
              {row.phone ? <> · {row.phone}</> : null}
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-sm"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="small-label block mb-1.5">How did you contact them?</label>
            <div className="flex items-center gap-2 flex-wrap">
              {MODE_OPTS.map((m) => (
                <button key={m.key} type="button" onClick={() => setMode(m.key)}
                  className={`h-10 px-4 text-xs font-semibold rounded-sm border flex items-center gap-1.5 ${mode === m.key ? "bg-black text-white border-black" : "bg-white text-gray-600 border-gray-300 hover:border-black"}`}
                  data-testid={`followup-mode-${m.key}`}>
                  <m.Icon size={13} /> {m.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="small-label block mb-1.5">Outcome / next step</label>
            <textarea
              className="input-lg min-h-[90px] py-2"
              placeholder="e.g. Spoke with Rohit, promised payment by Friday"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              data-testid="followup-comment"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="small-label block mb-1.5">Next follow-up date</label>
              <input type="date" className="input-lg" value={nextDate}
                onChange={(e) => setNextDate(e.target.value)}
                data-testid="followup-next-date" />
            </div>
            <div>
              <label className="small-label block mb-1.5">Status</label>
              <select className="input-lg" value={status} onChange={(e) => setStatus(e.target.value)}
                data-testid="followup-status">
                <option value="open">Open · still chasing</option>
                <option value="closed">Closed · settled / on hold</option>
              </select>
            </div>
          </div>
          {row.history?.length > 0 && (
            <div className="card-flat p-3">
              <button onClick={() => setShowHist((s) => !s)}
                className="text-xs text-gray-500 hover:text-black flex items-center gap-1.5 mb-2">
                <History size={13} /> {showHist ? "Hide" : "Show"} {row.history.length} previous {row.history.length === 1 ? "follow-up" : "follow-ups"}
              </button>
              {showHist && (
                <div className="space-y-2 max-h-48 overflow-auto">
                  {[...row.history].reverse().map((h, i) => (
                    <div key={i} className="text-xs border-l-2 border-gray-200 pl-3 py-1">
                      <div className="text-gray-500 mb-0.5">{fmtDate(h.date)} · {h.mode} · {h.by_email}</div>
                      {h.comment && <div className="text-gray-700">{h.comment}</div>}
                      {h.next_followup_date && (
                        <div className="text-[#1D4ED8] mt-0.5">Next: {fmtDate(h.next_followup_date)}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="p-4 border-t border-gray-100 flex items-center gap-2">
          <button disabled={busy} onClick={save}
            className="btn-primary flex items-center gap-1.5"
            data-testid="followup-save">
            <Save size={14} /> Log Follow-up
          </button>
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          {row.id && (
            <button onClick={del} className="ml-auto text-xs text-[#B91C1C] hover:underline flex items-center gap-1"
              data-testid="followup-delete">
              <Trash2 size={12} /> Clear record
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PaymentFollowupsPage() {
  const { auth } = useAuth();
  const today = todayISO();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [status, setStatus]     = useState("open");
  const [filter, setFilter]     = useState("due");
  const [amtBand, setAmtBand]   = useState("any");
  const [search, setSearch]     = useState("");
  const [modal, setModal]       = useState(null);  // row being logged

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/followups", { params: { status } });
      setData(d);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    let r = data?.rows || [];
    const q = search.trim().toLowerCase();
    if (q) r = r.filter((x) => x.party_name?.toLowerCase().includes(q));
    const minAmt = AMOUNT_BANDS.find((b) => b.key === amtBand)?.min || 0;
    if (minAmt > 0) r = r.filter((x) => x.balance >= minAmt);

    if (filter === "due")        r = r.filter((x) => !x.due_date || x.due_date <= today);
    else if (filter === "today") r = r.filter((x) => x.due_date === today);
    else if (filter === "overdue") r = r.filter((x) => x.due_date && x.due_date < today);
    else if (filter === "upcoming") r = r.filter((x) => x.due_date && x.due_date > today);
    else if (filter === "unscheduled") r = r.filter((x) => !x.due_date);
    return r;
  }, [data, search, amtBand, filter, today]);

  const summary = data?.summary || {};

  const ratingColor = (r) => {
    if (r === "A") return "bg-green-100 text-green-700";
    if (r === "B") return "bg-blue-100 text-blue-700";
    if (r === "C") return "bg-yellow-100 text-yellow-700";
    if (r === "D") return "bg-red-100 text-red-700";
    return "bg-gray-100 text-gray-500";
  };

  const updateRating = async (partyId, rating) => {
    try {
      await api.patch(`/customers/${partyId}/credit-rating`, { credit_rating: rating });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="payment-followups-page">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <CalendarClock size={22} className="text-black" />
          <h2 className="font-heading font-black text-2xl tracking-tight">Payment Follow-ups</h2>
        </div>
        <span className="text-xs text-gray-500 border border-gray-200 rounded-sm px-2 py-0.5">
          Overdue payment reminders · credit rating
        </span>
        <div className="ml-auto flex items-center gap-2">
          <div className="flex gap-1">
            {["open", "closed"].map((s) => (
              <button key={s} onClick={() => setStatus(s)}
                className={`px-3 h-9 rounded-sm text-xs font-semibold border ${status === s ? "bg-black text-white border-black" : "bg-white border-gray-300 hover:border-black"}`}
                data-testid={`fu-status-${s}`}>
                {s === "open" ? "Open" : "Closed"}
              </button>
            ))}
          </div>
          <button onClick={load}
            className="h-9 px-3 border border-gray-300 rounded-sm text-xs font-semibold inline-flex items-center gap-1.5 hover:border-black"
            data-testid="fu-refresh">
            <RefreshCcw size={13} /> Refresh
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card-flat p-5 bg-black text-white border-black">
          <div className="small-label text-white/70">Total outstanding</div>
          <div className="data-num text-3xl mt-1" data-testid="fu-total-pending">
            ₹ {fmt(summary.total_pending || 0)}
          </div>
          <div className="text-xs text-white/70 mt-1">{summary.count || 0} customers</div>
        </div>
        <div className="card-flat p-5 bg-[#FEE2E2] border-[#FECACA]">
          <div className="small-label text-[#B91C1C] flex items-center gap-1.5"><AlertTriangle size={12} /> Overdue follow-up</div>
          <div className="data-num text-3xl mt-1 text-[#B91C1C]" data-testid="fu-overdue">{summary.overdue_count || 0}</div>
        </div>
        <div className="card-flat p-5 bg-[#FEF3C7] border-[#FDE68A]">
          <div className="small-label text-[#B45309] flex items-center gap-1.5"><CalendarClock size={12} /> Due today</div>
          <div className="data-num text-3xl mt-1 text-[#B45309]" data-testid="fu-today">{summary.due_today_count || 0}</div>
        </div>
        <div className="card-flat p-5">
          <div className="small-label text-gray-500">Not scheduled</div>
          <div className="data-num text-3xl mt-1" data-testid="fu-unscheduled">{summary.unscheduled_count || 0}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap card-flat p-3">
        {FILTERS.map((f) => (
          <button key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 h-8 rounded-sm text-xs font-semibold border ${filter === f.key ? "bg-black text-white border-black" : "bg-white text-gray-600 border-gray-300 hover:border-black"}`}
            data-testid={`fu-filter-${f.key}`}>
            {f.label}
          </button>
        ))}
        <div className="h-6 w-px bg-gray-200 mx-1" />
        {AMOUNT_BANDS.map((b) => (
          <button key={b.key}
            onClick={() => setAmtBand(b.key)}
            className={`px-3 h-8 rounded-sm text-xs font-semibold border ${amtBand === b.key ? "border-black bg-gray-50" : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"}`}
            data-testid={`fu-amt-${b.key}`}>
            {b.label}
          </button>
        ))}
        <div className="ml-auto relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search party…"
            className="input-lg h-9 pl-8 w-44 text-sm" data-testid="fu-search" />
        </div>
      </div>

      {/* Table */}
      <div className="card-flat" data-testid="fu-table">
        <div className="table-wrap">
          <table className="table-dense w-full">
            <thead>
              <tr>
                <th className="text-left">Customer</th>
                <th className="num">Outstanding</th>
                <th className="text-left">Rating</th>
                <th className="text-left">Last follow-up</th>
                <th className="text-left">Next due</th>
                <th className="num">Count</th>
                <th className="text-left">Last note</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="py-8 text-center text-sm text-gray-400">Loading…</td></tr>}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-10 text-center text-sm text-gray-400">
                    <CheckCircle2 size={28} className="inline-block text-[#15803D] mb-1.5" />
                    <div>No overdue follow-ups. Great work!</div>
                  </td>
                </tr>
              )}
              {!loading && rows.map((r) => {
                const overdue = r.due_date && r.due_date < today;
                const dueToday = r.due_date === today;
                return (
                  <tr key={r.party_id} data-testid={`fu-row-${r.party_id}`}>
                    <td>
                      <div className="font-semibold text-sm">{r.party_name}</div>
                      <div className="text-xs text-gray-400 flex items-center gap-2">
                        {r.phone && (
                          <a href={`tel:${r.phone}`} className="flex items-center gap-1 hover:text-black">
                            <Phone size={11} /> {r.phone}
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="num">
                      <span className="data-num font-bold text-[#B91C1C]">₹ {fmt(r.balance)}</span>
                    </td>
                    <td>
                      <select
                        className={`h-7 px-2 rounded-sm text-xs font-semibold border-0 ${ratingColor(r.credit_rating)}`}
                        value={r.credit_rating || ""}
                        onChange={(e) => updateRating(r.party_id, e.target.value)}
                        data-testid={`fu-rating-${r.party_id}`}>
                        {CREDIT_RATINGS.map((v) => (
                          <option key={v} value={v}>{v ? `${v} — ${v === "A" ? "Excellent" : v === "B" ? "Good" : v === "C" ? "Average" : "Poor"}` : "No rating"}</option>
                        ))}
                      </select>
                    </td>
                    <td className="text-xs text-gray-500">
                      {r.last_followup_date ? fmtDate(r.last_followup_date) : "—"}
                    </td>
                    <td>
                      {r.due_date ? (
                        <span className={`text-xs font-semibold ${overdue ? "text-[#B91C1C]" : dueToday ? "text-[#B45309]" : "text-gray-600"}`}>
                          {overdue && <AlertTriangle size={11} className="inline mr-0.5" />}
                          {fmtDate(r.due_date)}
                          {r.days_overdue > 0 && <span className="text-[10px] ml-1">({r.days_overdue}d overdue)</span>}
                        </span>
                      ) : <span className="text-gray-400 text-xs">—</span>}
                    </td>
                    <td className="num text-sm">
                      <span className={r.followup_count > 0 ? "font-bold" : "text-gray-400"}>
                        {r.followup_count || 0}
                      </span>
                    </td>
                    <td className="text-xs text-gray-500 max-w-[160px] truncate">
                      {r.last_comment || "—"}
                    </td>
                    <td className="text-right">
                      <div className="inline-flex items-center gap-1.5">
                        {r.phone && (
                          <a href={`https://wa.me/${r.phone.replace(/\D/g, "")}?text=${encodeURIComponent(`Hello ${r.party_name}, payment of ₹${fmt(r.balance)} is due. Please arrange at the earliest.`)}`}
                            target="_blank" rel="noreferrer"
                            className="h-7 px-2 border border-gray-200 rounded-sm text-xs hover:border-green-500 hover:text-green-600 inline-flex items-center"
                            data-testid={`fu-wa-${r.party_id}`}>
                            <MessageSquare size={12} />
                          </a>
                        )}
                        <button
                          onClick={() => setModal(r)}
                          className="btn-primary h-8 px-3 text-xs"
                          data-testid={`fu-log-${r.party_id}`}>
                          Log
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <LogModal row={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}
    </div>
  );
}
